/**
 * The placeholder a pasted link leaves in the shopper's sentence.
 *
 * "Want to have something like https://www.amazon.ca/ViewSonic-PX700HDH-.../dp/B0DDQ3C46W?..."
 * becomes "Want to have something like ‹ViewSonic PX700HDH Projector›": the
 * address moves into a chip next to the "+", and a short, readable stand-in
 * keeps the link's place in the sentence, so the sentence still reads and the
 * model still knows which word the link answers.
 *
 * A token is `‹label›` where the label comes from the address alone
 * (linkTokenLabel): stable from the moment of the paste, never rewritten under
 * the shopper's fingers when the preview arrives. A token means something only
 * next to a link attachment with the same label: the Nth token with a label
 * belongs to the Nth link with that label. Anything that does not pair up is
 * ordinary text, so a hand-edited token degrades to words instead of breaking.
 *
 * Shared by the search bar (insert, move, delete), the conversation (draws an
 * inline pill) and the chat route (frames the link where it was written).
 * Client-safe: no Node APIs.
 */

export const TOKEN_OPEN = "‹"; // ‹
export const TOKEN_CLOSE = "›"; // ›
const LABEL_MAX_CHARS = 30;
const LABEL_MAX_WORDS = 4;

/** Path words a shop files every page under: they name no product. */
const PATH_NOISE = new Set([
  "product", "products", "produit", "produits", "producto", "productos", "item", "items", "itm", "ip", "p", "dp",
  "gp", "pd", "pdp", "shop", "store", "boutique", "tienda", "buy", "detail", "details", "catalog", "catalogue",
  "collections", "collection", "html", "htm", "php", "aspx", "index", "view", "www", "s", "c", "b",
]);

const LOCALE_SEGMENT = /^[a-z]{2}(?:-[a-z]{2})?$/i;

/** Share-link hosts: their path is a random id, never a name. */
const SHORT_LINK_HOSTS = new Set(["a.co", "amzn.to", "amzn.eu", "amzn.asia", "amzn.com", "bit.ly", "t.co", "tinyurl.com"]);

function cleanLabel(value: string): string {
  return value
    .replace(new RegExp(`[${TOKEN_OPEN}${TOKEN_CLOSE}\\[\\]\\r\\n\\t]`, "g"), " ")
    .replace(/\s+/g, " ")
    .trim();
}

function wordsOfSegment(segment: string): string[] {
  let text = segment;
  try {
    text = decodeURIComponent(segment);
  } catch {
    // keep the raw segment
  }
  return text
    .replace(/\.(?:html?|php|aspx?)$/i, "")
    .split(/[-_+\s]+/)
    .filter((word) => {
      if (!word) return false;
      // Bare ids: long digit runs, and ASIN-like codes (10+ letters+digits).
      if (/^\d{5,}$/.test(word)) return false;
      if (/^[a-z0-9]{10,}$/i.test(word) && /\d/.test(word) && /[a-z]/i.test(word)) return false;
      return true;
    });
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The short name a link goes by in a sentence: its address's product words, else its host. */
export function linkTokenLabel(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return cleanLabel(String(url)).slice(0, LABEL_MAX_CHARS) || "link";
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
  const segments = parsed.pathname.split("/").filter(Boolean);

  if (SHORT_LINK_HOSTS.has(host)) return cleanLabel(host);

  let candidate: string | undefined;
  if (/(?:^|\.)amazon\./.test(host)) {
    const dp = segments.findIndex((s) => /^dp$/i.test(s));
    if (dp > 0) candidate = segments[dp - 1]; // amazon.ca/<slug>/dp/<ASIN>
    else if (dp === 0) return cleanLabel(host);
  }
  if (!candidate) {
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i];
      if (PATH_NOISE.has(segment.toLowerCase()) || LOCALE_SEGMENT.test(segment)) continue;
      if (wordsOfSegment(segment).filter((w) => /\p{L}{2,}/u.test(w)).length === 0) continue;
      candidate = segment;
      break;
    }
  }

  const words = candidate ? wordsOfSegment(candidate).filter((word) => !PATH_NOISE.has(word.toLowerCase())) : [];
  const picked: string[] = [];
  for (const word of words) {
    const next = [...picked, word].join(" ");
    if (picked.length >= LABEL_MAX_WORDS || (picked.length > 0 && next.length > LABEL_MAX_CHARS)) break;
    picked.push(word);
  }
  let label = cleanLabel(picked.join(" "));
  if (label.length > LABEL_MAX_CHARS) label = `${label.slice(0, LABEL_MAX_CHARS - 1).trim()}…`;
  return capitalize(label) || cleanLabel(host) || "link";
}

export function linkTokenText(label: string): string {
  return `${TOKEN_OPEN}${cleanLabel(label)}${TOKEN_CLOSE}`;
}

export interface LinkTokenRange {
  start: number;
  /** Exclusive. */
  end: number;
  label: string;
  /** Index into the labels given to findLinkTokens, or null for a token with no link to answer to. */
  linkIndex: number | null;
}

/** Every token of a known label in `text`, in text order, paired with its link. */
export function findLinkTokens(text: string, labels: readonly string[]): LinkTokenRange[] {
  const out: LinkTokenRange[] = [];
  if (!text || !text.includes(TOKEN_OPEN)) return out;
  const seen = new Set<string>();
  labels.forEach((label) => {
    if (!label || seen.has(label)) return;
    seen.add(label);
    const owners: number[] = [];
    labels.forEach((other, index) => {
      if (other === label) owners.push(index);
    });
    const token = linkTokenText(label);
    let from = 0;
    let nth = 0;
    for (;;) {
      const at = text.indexOf(token, from);
      if (at === -1) break;
      out.push({ start: at, end: at + token.length, label, linkIndex: owners[nth] ?? null });
      nth += 1;
      from = at + token.length;
    }
  });
  return out.sort((a, b) => a.start - b.start);
}

/** `text` with each paired token replaced by `replacer(linkIndex)`; which links it named. */
export function replaceLinkTokens(
  text: string,
  labels: readonly string[],
  replacer: (linkIndex: number) => string,
): { text: string; referenced: Set<number> } {
  const referenced = new Set<number>();
  let out = "";
  let cursor = 0;
  for (const range of findLinkTokens(text, labels)) {
    if (range.linkIndex === null) continue;
    out += text.slice(cursor, range.start) + replacer(range.linkIndex);
    cursor = range.end;
    referenced.add(range.linkIndex);
  }
  return { text: out + text.slice(cursor), referenced };
}

/** The shopper's own words: `text` without any token (paired or not). */
export function stripLinkTokens(text: string, labels: readonly string[]): string {
  let out = "";
  let cursor = 0;
  for (const range of findLinkTokens(text, labels)) {
    out += text.slice(cursor, range.start) + " ";
    cursor = range.end;
  }
  return (out + text.slice(cursor)).replace(/\s+/g, " ").trim();
}

/** Take one token out of the text, and the one extra space it leaves behind. */
export function removeLinkToken(text: string, range: Pick<LinkTokenRange, "start" | "end">): string {
  const before = text.slice(0, range.start);
  let after = text.slice(range.end);
  if ((before === "" || /\s$/.test(before)) && /^\s/.test(after)) after = after.replace(/^\s/, "");
  else if (/\s$/.test(before) && after === "") return before.replace(/\s+$/, "");
  return before + after;
}

/**
 * Put a piece of text into the sentence over the selection [start, end), with
 * a space on each side where the sentence needs one. Returns the new text and
 * where the caret goes: just after the piece and its space.
 */
export function insertInlineText(
  text: string,
  start: number,
  end: number,
  piece: string,
): { text: string; caret: number } {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  const before = text.slice(0, from);
  const after = text.slice(to);
  const lead = before && !/\s$/.test(before) ? " " : "";
  const trail = !after || (!/^\s/.test(after) && !/^[.,;:!?)\]}]/.test(after)) ? " " : "";
  const inserted = `${lead}${piece}${trail}`;
  return { text: before + inserted + after, caret: before.length + inserted.length };
}

/** A link's placeholder, put into the sentence over the selection. */
export function insertLinkToken(
  text: string,
  start: number,
  end: number,
  label: string,
): { text: string; caret: number } {
  return insertInlineText(text, start, end, linkTokenText(label));
}
